import { c as _c } from "react/compiler-runtime";
import { add, differenceInSeconds, setMilliseconds, startOfDay, startOfHour, sub } from 'date-fns';
import { Formik, useField } from 'formik';
import { FormEvent } from 'react';
import { EventListFiltersProps } from '../../../../typings/EventList.interface';
import { RequestListFiltersProps } from '../../../../typings/RequestList.interface';
import { chart_resolutions, getBestResolution, getTime, startOfTime, timebar_resolutions } from '../../../../utils/chart';
import { formatDate, getTimezoneAbbreviation, RelativeDate, resolveRelativeDate } from '../../../../utils/date';
import { useDatePreferences, useUpdateTimezonePreference } from '../../../hooks/useDatePreferences';
import { getTimerangeLabel } from '../../../scenes/DashboardScene/Events/Histogram';
import Button from '../../base/Button';
import { StyledCardSection } from '../../base/Card';
import Text from '../../base/Text';
import Dropdown from '../../Dropdown';
import DatePickerInput, { formatDateFilterValues } from '../../Form/Fields/DatePickerInput';
import { BROWSER_TIMEZONE_OPTION, resolveTimezoneOption } from '../../Form/Fields/TimezoneSelectInput';
import { Div } from '../../helpers/StyledUtils';
const DateFilter: React.FC<{
  retention_days: number;
  filters: EventListFiltersProps | RequestListFiltersProps;
  delete_button?: JSX.Element;
  onSubmit: (e?: FormEvent<HTMLFormElement>) => void;
}> = t0 => {
  const $ = _c(42);
  const {
    retention_days,
    delete_button,
    onSubmit
  } = t0;
  const {
    browser_timezone,
    timezone,
    uses_browser_timezone
  } = useDatePreferences();
  const updateTimezonePreference = useUpdateTimezonePreference();
  const [, t1, t2] = useField("date");
  const {
    value
  } = t1;
  const {
    setValue,
    setTouched
  } = t2;
  let date_label;
  let t3;
  let t4;
  let t5;
  if ($[0] !== retention_days || $[1] !== timezone || $[2] !== value) {
    const new_date = {
      ...value
    };
    if (new_date?.relative) {
      const {
        min,
        max
      } = resolveRelativeDate(new_date.relative);
      new_date.min = min.toISOString();
      new_date.max = max.toISOString();
    }
    const now = new Date();
    const end_date = setMilliseconds(new_date?.max ? new Date(new_date?.max) : add(startOfHour(now), {
      hours: 1
    }), 0);
    const start_date = setMilliseconds(new_date?.min ? new Date(new_date?.min) : startOfDay(sub(now, {
      seconds: retention_days * 24 * 60 * 60
    })), 0);
    let [start_date_0, end_date_0] = [start_date, end_date];
    const diff = differenceInSeconds(end_date_0, start_date_0);
    const resolutions = {
      chart_resolution: getBestResolution(diff, 60, chart_resolutions).best,
      axis_resolution: getBestResolution(diff, 10, timebar_resolutions).best
    };
    const {
      chart_resolution
    } = resolutions;
    if (chart_resolution.granularity !== "second") {
      const granularity_key = `${chart_resolution.granularity}s`;
      end_date_0 = add(startOfTime[chart_resolution.granularity](end_date_0), {
        [granularity_key]: Math.floor((getTime[chart_resolution.granularity](end_date_0) + 1) / chart_resolution.unit) * chart_resolution.unit
      });
      start_date_0 = sub(end_date_0, {
        seconds: diff
      });
    }
    let t6;
    if ($[7] !== timezone || $[8] !== value) {
      t6 = getTimerangeLabel(value, timezone);
      $[7] = timezone;
      $[8] = value;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    date_label = t6;
    t3 = {
      ...value,
      min: value?.min || start_date_0.toISOString(),
      max: value?.max || end_date_0.toISOString()
    } as {
      relative?: RelativeDate;
      min?: string;
      max?: string;
    };
    t4 = value?.min ? formatDate(value.min, "HH:mm:ss", timezone) : formatDate(start_date_0, "HH:mm:ss", timezone);
    t5 = value?.max ? formatDate(value.max, "HH:mm:ss", timezone) : formatDate(end_date_0, "HH:mm:ss", timezone);
    $[0] = retention_days;
    $[1] = timezone;
    $[2] = value;
    $[3] = date_label;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    date_label = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
  }
  const t6 = uses_browser_timezone ? BROWSER_TIMEZONE_OPTION : timezone;
  let t7;
  if ($[10] !== t3 || $[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = {
      date: t3,
      start_time: t4,
      end_time: t5,
      timezone: t6
    };
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const form_initial_values = t7;
  let t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      align: "center"
    };
    t9 = {
      width: "fit-content"
    };
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  let t10;
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      align: "baseline",
      gap: 1
    };
    t11 = {
      r: -1
    };
    $[17] = t10;
    $[18] = t11;
  } else {
    t10 = $[17];
    t11 = $[18];
  }
  let t12;
  if ($[19] !== date_label) {
    t12 = <span>{date_label}</span>;
    $[19] = date_label;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== timezone) {
    t13 = getTimezoneAbbreviation(timezone);
    $[21] = timezone;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t13) {
    t14 = <Text as="span" muted={true} semi={true} size="xs">{t13}</Text>;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t12 || $[26] !== t14) {
    t15 = <Div flex={t10} m={t11}>{t12}{t14}</Div>;
    $[25] = t12;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== browser_timezone || $[29] !== form_initial_values || $[30] !== onSubmit || $[31] !== retention_days || $[32] !== setTouched || $[33] !== setValue || $[34] !== updateTimezonePreference) {
    t16 = toggle => <Formik initialValues={form_initial_values} onSubmit={async v => {
      const selected_timezone = resolveTimezoneOption(v.timezone, browser_timezone);
      await updateTimezonePreference(v.timezone === BROWSER_TIMEZONE_OPTION ? null : v.timezone);
      const {
        start_date: start_date_1,
        end_date: end_date_1
      } = formatDateFilterValues({
        start_date: v.date.min,
        start_time: v.start_time,
        end_date: v.date.max,
        end_time: v.end_time
      }, selected_timezone);
      setTouched(true);
      setValue({
        ...v.date,
        min: start_date_1,
        max: end_date_1
      });
      toggle(false);
      onSubmit();
    }}>{props => <StyledCardSection><DatePickerInput display_reset={false} name="date" time_format="hh:mm:ss" retention_days={retention_days} /><Div flex={{
          justify: "flex-end",
          gap: 3
        }} p={{
          t: 0,
          x: 4,
          b: 4
        }}><Button outline={true} onClick={() => toggle(false)}>Cancel</Button><Button primary={true} onClick={() => props.handleSubmit()}>Apply</Button></Div></StyledCardSection>}</Formik>;
    $[28] = browser_timezone;
    $[29] = form_initial_values;
    $[30] = onSubmit;
    $[31] = retention_days;
    $[32] = setTouched;
    $[33] = setValue;
    $[34] = updateTimezonePreference;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t15 || $[37] !== t16) {
    t17 = <Dropdown outline={true} large={true} label={t15}>{t16}</Dropdown>;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  const t18 = delete_button && delete_button;
  let t19;
  if ($[39] !== t17 || $[40] !== t18) {
    t19 = <Div flex={t8} style={t9}>{t17}{t18}</Div>;
    $[39] = t17;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  return t19;
};
export default DateFilter;